import React, { lazy, useState, useEffect, useRef, useCallback } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import ConsentBanner from "./components/ConsentBanner";
import AccessibleNavigationAnnouncer from "./components/AccessibleNavigationAnnouncer";
import authContext from "./context/authContext";
import userContext from "./context/userContext";
import subscriptionContext from "./context/subscriptionContext";
import newPaymentContext from "./context/newPayment";
import { isAuthenticated } from "./service/auth";
import { getSubscriptionFromDB } from "./service/payments";

// Dashboard shell (Stripe.js, private route, layout) is only loaded for /app/*
const AppShell = lazy(() => import("./containers/AppShell"));
const Login = lazy(() => import("./pages/Login"));
const CreateAccount = lazy(() => import("./pages/CreateAccount"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const Landing = lazy(() => import("./pages/LandingPage"));
const Verify = lazy(() => import("./pages/verify"));
const Activate = lazy(() => import("./pages/Activate"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Docs = lazy(() => import("./pages/Docs"));
const Pricing = lazy(() => import("./pages/Pricing"));
const UuleTool = lazy(() => import("./pages/UuleTool"));
const SearchUrlBuilder = lazy(() => import("./pages/SearchUrlBuilder"));
const ToolsIndex = lazy(() => import("./pages/ToolsIndex"));
const TbsTool = lazy(() => import("./pages/TbsTool"));
const GoogleCountries = lazy(() => import("./pages/GoogleCountries"));
const GoogleLanguages = lazy(() => import("./pages/GoogleLanguages"));
const SerpPreview = lazy(() => import("./pages/SerpPreview"));
const UuleCities = lazy(() => import("./pages/UuleCities"));
const UuleCountry = lazy(() => import("./pages/UuleCountry"));
const Contact = lazy(() => import("./pages/Contact"));
const NoMatch = lazy(() => import("./pages/404"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));

function App() {
  const [authentication, setAuthentication] = useState();
  const [user, setUser] = useState("");
  const [subscription, setSubscription] = useState(null);
  const [newPayment, setNewPayment] = useState(null);

  // Track if auth has been checked to prevent duplicate calls
  const authCheckedRef = useRef(false);

  const getAuth = useCallback(async () => {
    // Prevent duplicate auth checks
    if (authCheckedRef.current) return;
    authCheckedRef.current = true;

    const auth = await isAuthenticated();
    if (auth !== undefined) {
      setAuthentication(true);
      setUser(auth);
    }
  }, []);

  const getSubscription = useCallback(async (email) => {
    if (email) {
      const sub = await getSubscriptionFromDB(email);
      setSubscription(sub);
    }
  }, []);

  // Initial auth check - runs only once on mount
  useEffect(() => {
    getAuth();
  }, [getAuth]);

  // Fetch subscription when user becomes available
  useEffect(() => {
    if (authentication && user?.email) {
      getSubscription(user.email);
    }
  }, [authentication, user?.email, getSubscription]);

  return (
    <>
      <authContext.Provider value={{ authentication, setAuthentication }}>
        <userContext.Provider value={{ user, setUser }}>
          <subscriptionContext.Provider
            value={{ subscription, setSubscription }}
          >
            <newPaymentContext.Provider value={{ newPayment, setNewPayment }}>
              <Router>
                <AccessibleNavigationAnnouncer />
                <ConsentBanner />
                <Routes>
                  <Route path="/" element={<Landing />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<CreateAccount />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/email/verify" element={<Verify />} />
                  <Route path="/docs" element={<Docs />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/tools/google-search-uule-generator-decoder" element={<UuleTool />} />
                  <Route path="/tools/google-search-url-builder" element={<SearchUrlBuilder />} />
                  <Route path="/tools" element={<ToolsIndex />} />
                  <Route path="/tools/google-tbs-parameter-builder" element={<TbsTool />} />
                  <Route path="/tools/google-countries-domains" element={<GoogleCountries />} />
                  <Route path="/tools/google-language-codes" element={<GoogleLanguages />} />
                  <Route path="/tools/serp-preview" element={<SerpPreview />} />
                  <Route path="/tools/uule-locations" element={<UuleCities />} />
                  <Route path="/tools/uule-locations/:slug" element={<UuleCountry />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/termsofservice" element={<TermsOfService />} />
                  <Route path="/users/activate/:token" element={<Activate />} />
                  <Route
                    path="/users/reset/:token/:identifier"
                    element={<ResetPassword />}
                  />
                  <Route
                    path="/app/*"
                    element={<AppShell />}
                  />
                  <Route path="*" element={<NoMatch />} />
                </Routes>
              </Router>
            </newPaymentContext.Provider>
          </subscriptionContext.Provider>
        </userContext.Provider>
      </authContext.Provider>
    </>
  );
}

export default App;
